import React, { useMemo, useContext, useEffect, useState } from "react";
import StarekContext from "../../context/StarekContext";
import { gql, useMutation, useQuery } from "@apollo/client";
import { SidebarView, SidebarContextProvider } from "../../components/Sidebar";
import RoleBasicDetails from "./RoleBasicDetails";
import RolePermissions from "./RolePermissions";

const CREATE_ONE_ROLE = gql`
    mutation CreateOneRole($data: RoleCreateInput!) {
        createOneRole(data: $data) {
            id
            name
            description
            is_active
            privileges {
                id
                entity_type_id
                permission
            }
        }
    }
`;

const UPDATE_ONE_ROLE = gql`
    mutation UpdateOneRole(
        $where: RoleWhereUniqueInput!
        $data: RoleUpdateInput!
    ) {
        updateOneRole(where: $where, data: $data) {
            id
            name
            description
            is_active
            privileges {
                id
                entity_type_id
                permission
            }
        }
    }
`;

const ENTITY_TYPES_QUERY = gql`
    query EntityType {
        entityTypes {
            id
            code
            singular
            plural
            type
            description
            is_active
        }
    }
`;

const TYPE = {
    PERMISSION: 1,
    ASSOCIATIONS: 2,
    ACTION: 4,
};

const KEYS_BY_TYPE = {
    [TYPE.PERMISSION]: ["read", "modify", "delete"],
    [TYPE.ASSOCIATIONS]: ["link", "unlink"],
    [TYPE.ACTION]: ["execute"],
};

const shapeForType = (t) =>
    Object.fromEntries((KEYS_BY_TYPE[t] || []).map((k) => [k, false]));

function RoleSidebar({ show, item, onDiscard, onSave, onDelete }) {
    const starekContext = useContext(StarekContext);
    const [preparedEntity, setPreparedEntity] = useState(null);

    const [createOneRole] = useMutation(CREATE_ONE_ROLE, {
        client: starekContext.engineClient,
    });

    const [updateOneRole] = useMutation(UPDATE_ONE_ROLE, {
        client: starekContext.engineClient,
    });

    // Query entity types
    const { data: entityTypesData, loading: loadingEntityTypes } = useQuery(
        ENTITY_TYPES_QUERY,
        {
            client: starekContext.engineClient,
        },
    );

    const entityTypes = useMemo(
        () => (entityTypesData?.entityTypes || []).filter((e) => e?.is_active),
        [entityTypesData],
    );

    const permissionFlags = starekContext?.types?.permissions?.flags || {};

    const permissionMasks = useMemo(() => {
        const maskRead = permissionFlags.BIT_MASK_READ ?? 1;
        const maskWrite = permissionFlags.BIT_MASK_WRITE ?? 2;
        const maskExec = permissionFlags.BIT_MASK_EXECUTEL ?? 4;
        const maskAll = permissionFlags.BIT_MASK_ALL ?? maskWrite;
        const maskLink =
            permissionFlags.BIT_LOCATION_LINK != null
                ? 1 << permissionFlags.BIT_LOCATION_LINK
                : maskRead;
        const maskUnlink =
            permissionFlags.BIT_LOCATION_UNLINK != null
                ? 1 << permissionFlags.BIT_LOCATION_UNLINK
                : maskWrite;

        return { maskRead, maskWrite, maskExec, maskAll, maskLink, maskUnlink };
    }, [permissionFlags]);

    // Convert privileges array to permissions object for editing
    const initialPermissions = useMemo(() => {
        if (!item?.privileges) return {};

        const rawPrivileges = item.privileges;
        const incoming = {};

        for (const priv of rawPrivileges) {
            const entityTypeId = priv.entity_type_id;
            const permValue =
                typeof priv.permission === "number" ? priv.permission : 0;

            incoming[entityTypeId] = permValue;
        }

        return incoming;
    }, [item]);

    // Prepare entity with permissions when item or entityTypes change
    useEffect(() => {
        if (!show) {
            return;
        }

        if (loadingEntityTypes || entityTypes.length === 0) {
            return;
        }

        const permissions = {};

        if (!item || Object.keys(initialPermissions).length === 0) {
            // New role: initialize all permissions to false
            for (const et of entityTypes) {
                permissions[et.code] = shapeForType(et.type);
            }
        } else {
            // Editing: convert bitmask permissions to checkboxes
            const entityById = new Map(entityTypes.map((et) => [et.id, et]));

            for (const [entityTypeId, permValue] of Object.entries(
                initialPermissions,
            )) {
                const et = entityById.get(Number(entityTypeId));
                if (!et) continue;

                const base = shapeForType(et.type);

                if (et.type === TYPE.PERMISSION) {
                    base.read =
                        (permValue & permissionMasks.maskRead) ===
                        permissionMasks.maskRead;
                    base.modify =
                        (permValue & permissionMasks.maskWrite) ===
                        permissionMasks.maskWrite;
                    base.delete =
                        (permValue & permissionMasks.maskAll) ===
                        permissionMasks.maskAll;
                } else if (et.type === TYPE.ASSOCIATIONS) {
                    base.link =
                        (permValue & permissionMasks.maskLink) ===
                        permissionMasks.maskLink;
                    base.unlink =
                        (permValue & permissionMasks.maskUnlink) ===
                        permissionMasks.maskUnlink;
                } else if (et.type === TYPE.ACTION) {
                    base.execute =
                        (permValue & permissionMasks.maskExec) ===
                            permissionMasks.maskExec ||
                        (permValue & permissionMasks.maskAll) ===
                            permissionMasks.maskAll;
                }

                permissions[et.code] = base;
            }

            // Fill in missing entity types with defaults
            for (const et of entityTypes) {
                if (!permissions[et.code]) {
                    permissions[et.code] = shapeForType(et.type);
                }
            }
        }

        setPreparedEntity({
            ...item,
            name: item?.name || "",
            description: item?.description || "",
            permissions,
            entityTypes, // Include entity types in the prepared entity
        });
    }, [
        show,
        item,
        entityTypes,
        initialPermissions,
        permissionMasks,
        loadingEntityTypes,
    ]);

    const handleSaveInternal = async (emittedData) => {
        try {
            const { name, description, permissions } = emittedData;

            if (!name?.trim()) {
                starekContext.showError("Role name is required");
                return;
            }

            // Convert permissions object back to privileges array with bitmasks
            const privileges = [];
            const entityByCode = new Map(
                entityTypes.map((et) => [et.code, et]),
            );

            for (const [code, perms] of Object.entries(permissions || {})) {
                const et = entityByCode.get(code);
                if (!et) continue;

                let bitmask = 0;

                if (et.type === TYPE.PERMISSION) {
                    if (perms.read) bitmask |= permissionMasks.maskRead;
                    if (perms.modify) bitmask |= permissionMasks.maskWrite;
                    if (perms.delete) bitmask |= permissionMasks.maskAll;
                } else if (et.type === TYPE.ASSOCIATIONS) {
                    if (perms.link) bitmask |= permissionMasks.maskLink;
                    if (perms.unlink) bitmask |= permissionMasks.maskUnlink;
                } else if (et.type === TYPE.ACTION) {
                    if (perms.execute) bitmask |= permissionMasks.maskExec;
                }

                if (bitmask > 0) {
                    privileges.push({
                        entity_type: { connect: { id: et.id } },
                        permission: bitmask,
                    });
                }
            }

            const roleData = {
                name: name.trim(),
                description: description?.trim() || "",
                is_active: true,
            };

            if (!item) {
                // Create mode
                await createOneRole({
                    variables: {
                        data: {
                            ...roleData,
                            privileges: {
                                create: privileges,
                            },
                        },
                    },
                });
            } else {
                // Update mode
                await updateOneRole({
                    variables: {
                        where: { id: Number(item.id) },
                        data: {
                            name: { set: roleData.name },
                            description: { set: roleData.description },
                            privileges: {
                                deleteMany: {
                                    role_id: { equals: Number(item.id) },
                                },
                                create: privileges,
                            },
                        },
                    },
                });
            }

            if (onSave) {
                await onSave();
            }

            // Show success message
            starekContext.showSuccess(
                item
                    ? "Role updated successfully"
                    : "Role created successfully",
            );
        } catch (error) {
            console.error("Error saving role:", error);
            starekContext.showError(
                error?.message || "Failed to save role. Please try again.",
            );
            throw error; // Re-throw to let SidebarView know save failed
        }
    };

    const handleDeleteInternal = async () => {
        try {
            if (onDelete) {
                await onDelete(item);
            }
        } catch (error) {
            console.error("Error deleting role:", error);
            starekContext.showError(
                error?.message || "Failed to delete role. Please try again.",
            );
            throw error; // Re-throw to let caller handle if needed
        }
    };

    const handleDiscardInternal = async () => {
        try {
            if (onDiscard) {
                await onDiscard();
            }
        } catch (error) {
            console.error("Error discarding changes:", error);
            // Silently handle discard errors as they're less critical
        }
    };

    if (loadingEntityTypes) {
        return null; // Don't show sidebar until entity types are loaded
    }

    return (
        <SidebarContextProvider
            entity={preparedEntity}
            show={show}
            onSave={handleSaveInternal}
            onDelete={item?.id ? handleDeleteInternal : null}
            onDiscard={handleDiscardInternal}
        >
            <SidebarView entityName="Role">
                <SidebarView.Page
                    key={item?.id || "new"}
                    name="basic_details"
                    title="Basic Details"
                >
                    <RoleBasicDetails />
                </SidebarView.Page>
                <SidebarView.Page
                    key={`permissions-${item?.id || "new"}`}
                    name="permissions"
                    title="Permissions"
                >
                    <RolePermissions />
                </SidebarView.Page>
            </SidebarView>
        </SidebarContextProvider>
    );
}

export default RoleSidebar;
